
// Import JS Modules
import { builderDispatch, WebUI, PrismUI } from '../../_utils/ui';
import translate from '../../_utils/tools/translate';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import assign from 'lodash/assign';
import uniq from 'lodash/uniq';
import sortBy from 'lodash/sortBy';
import keys from 'lodash/keys';
import compact from 'lodash/compact';
import find from 'lodash/find';
import includes from 'lodash/includes';
import templateInterrogator from '../misc/template-interrogator';
import { usePrismItemStore } from 'stores/usePrismItemStore';

// -----------------------------------------------------------
//  TemplateStore
// -----------------------------------------------------------
var TemplateStore = {
  fetch() {
    // need prismItem to be loaded here?
    const prismItem = usePrismItemStore.getState().prismItem;

    fetch(`/m/t/${prismItem.id}`, {
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response;
    })
      .then((response) => response.json())
      .then((templates) => {
        this.templates = templates;
        this._template_cache = {};
        this._designs = {};
        this._layouts = {};

        this._all_templates = flatten(map(this.templates, (e) => map(e.templates, (template) => {
          const [design, layout, position] = TemplateStore.splitTemplateID(template.id);
          const info = assign({}, template, {
            design,
            layout,
            position,
          });

          TemplateStore._template_cache[template.id] = info;
          TemplateStore._designs[info.design] = TemplateStore._designs[info.design] || info;
          TemplateStore._layouts[info.layout] = TemplateStore._layouts[info.layout] || info;

          return info;
        })));

        builderDispatch.dispatch({ type: WebUI.TEMPLATES_LOADED });
      })
      .catch((e) => console.error(e));
  },
  templatesFiltered(filters) {
    const templates = flatten(map(this.templates, 'templates'));
    return templateInterrogator.filterTemplates(templates, { sections: templates, ...filters });
  },
  list() {
    return this.templates;
  },
  positions() {
    return uniq(sortBy(map(keys(TemplateStore._template_cache), (id) => TemplateStore._template_cache[id].position), (position) => position));
  },
  favourites() {
    return sortBy(map(keys(TemplateStore._designs), (design) => TemplateStore._designs[design]), (template) => template.id);
  },
  layouts() {
    return sortBy(map(keys(TemplateStore._layouts), (layout) => TemplateStore._layouts[layout]), (template) => template.id);
  },
  favouritesForPosition(positions, section) {
    const list = compact(keys(TemplateStore._designs)
      .map((design) => find(TemplateStore._all_templates,
        (template) => template.design === design
          && includes(template.sections, section)
          && includes(positions, template.position))));

    return sortBy(list, (template) => [parseInt(template.design, 10), template.id]);
  },
  layoutsForPosition(positions, section) {
    return sortBy(compact(map(keys(TemplateStore._layouts), (layout) => find(TemplateStore._all_templates, (template) => template.layout === layout
      && includes(template.sections, section)
      && includes(positions, template.position)))), (template) => [!includes(template.tags, 'popular'), template.id]);
  },
  templateExists(id) {
    return this.templates && this._template_cache[id];
  },
  // returns a template for a section that will have the maximum number of photo slots to fit the number specified.
  getTemplateByPhotoSlots(noOfPhotos, section, orientationTag) {
    let i; let max; const
      templates = this.templatesFiltered({
        section,
        orientations: orientationTag
      });

    if (templates.length < 1) return;

    for (i = 0; i < templates.length; i++) {
      if (max) { // keep in memory max templates available, so we can return without extra recursion in case the noOfPhotos exceeds it.
        if (max['no-photo'] < templates[i]['no-photos']) max = template[i];
      } else max = templates[i];

      // break the loop and return a template if there is a match
      if (templates[i]['no-photos'] == noOfPhotos) return templates[i];
    }

    if (max['no-photos'] > noOfPhotos) return max;

    // Call itself with - 1 photo count until a match is found.
    return this.getTemplateByPhotoSlots(noOfPhotos - 1, section);
  },
  wrapTitles: {
    'wrap-white': 'White',
    'wrap-black': 'Black',
    'wrap-full': 'Gallery',
    'wrap-gallery': 'Wrapped Edge',
    'wrap-mirror': 'Mirrored Edge',
    'wrap-color': `${translate('Colored')} Edge`,
  },
  currentWrap(template_id) {
    if (!template_id) {
      if (!window.activeView || !activeView.template) return;
      template_id = activeView.template.id;
    }

    return TemplateStore.wraps().find((template) => groupHasTemplate(template.group));

    function groupHasTemplate(group) {
      return group.templates.some((template) => template.id === template_id);
    }
  },
  getWrapGroup(type) {
    if (typeof type === 'object' && type.wrap) {
      return type;
    }

    return TemplateStore.wraps().find((template) => template.wrap == type);
  },
  templateGroup(template_id) {
    if (!template_id) return;

    let group = template_id.replace(/\/[^\/]*$/, '');
    if (group.length === 0) group = '.';
    return group;
  },
  templateName(template_id) {
    if (!template_id) return;
    return template_id.split(/\//).pop();
  },
  templateForWrap(type, current_template_id) {
    const wrap = this.getWrapGroup(type);
    if (!wrap) {
      console.error('Unable to find wrapGroup %s in theme', type);
      return;
    }
    for (let i = 0; i < wrap.group.templates.length; i++) {
      const template = wrap.group.templates[i];
      if (this.templateName(template.id) === this.templateName(current_template_id)) {
        return template.id;
      }
    }
    console.error('Unable to find template of type %s that matches %s', type, current_template_id);
  },
  get hasWraps() {
    return TemplateStore.wraps().length > 0;
  },
  wraps() {
    const wraps = [];

    if (!this.templates) return wraps;

    for (let i = 0; i < this.templates.length; i++) {
      const group = this.templates[i];
      const wrap_type = groupIsWrap(group);

      if (wrap_type) {
        wraps.push({
          id: group.id,
          wrap: wrap_type,
          title: this.wrapTitles[wrap_type],
          group,
        });
      }
    }

    return wraps;

    function templateWrapTag(template) {
      if (!template || !template.tags) return null;
      for (let i = 0; i < template.tags.length; i++) {
        if (template.tags[i].match(/^wrap-/)) {
          return template.tags[i];
        }
      }
    }

    function groupIsWrap(group) {
      const initial = templateWrapTag(group.templates[0]);
      if (initial && group.templates.length) {
        return initial;
      }

      for (let i = 1; i < group.templates.length; i++) {
        const tag = templateWrapTag(group.templates[i]);
        if (tag !== initial) {
          return false;
        }
      }
      return initial;
    }
  },
  getPortrait() {
    const cur = activeView.template.id;
    let alt;
    if (cur.indexOf('landscape') > -1) {
      alt = cur.replace(/landscape/, 'portrait');
    }
    if (this.templateExists(alt)) {
      return alt;
    }
    alt = cur;
    if (cur.indexOf('portrait') === -1) {
      alt = `${cur}-portrait`;
    }
    if (this.templateExists(alt)) {
      return alt;
    }
    return cur;
  },
  getLandscape() {
    const cur = activeView.template.id;
    let alt;
    if (cur.indexOf('portrait') > -1) {
      alt = cur.replace(/portrait/, 'landscape');
    }
    if (this.templateExists(alt)) {
      return alt;
    }
    alt = cur.replace(/-portrait/, '');
    if (this.templateExists(alt)) {
      return alt;
    }
    return cur;
  },
  splitTemplateID(templateID) {
    const bits = templateID.split(/\//);
    const layout = bits.pop(); let design; let side; let
      position = '*';

    // heuristic - this should only apply to albums
    if (bits[bits.length - 1] && (bits[bits.length - 1].match(/_(left|right|title)/))) {
      side = bits.pop().split(/_/);

      design = bits.join('/');
      if (design.length) design = `${design}/`;
      design = `${design + side[0]}_${side[1]}`;
      position = side[1];
    } else {
      design = bits.join('/');
      if (design.length) design = `${design}/`;
    }
    // console.log("%s: %s", templateID, JSON.stringify([design, layout, position]))
    return [design, layout, position];
  },
  getPosition(templateID) {
    return this.splitTemplateID(templateID)[2];
  },
  getLayout(templateID) {
    return this.splitTemplateID(templateID)[1];
  },
  getDesign(templateID) {
    return this.splitTemplateID(templateID)[0];
  },
  getTemplateByDesignAndLayout(design, layout) {
    const info = find(this._all_templates, (template) => (template.design === design) && (template.layout === layout));
    if (info) {
      return info.id;
    }
  },
  getDesignEquivalent(originalTemplateID, newTemplateID) {
    const layout = this.getLayout(originalTemplateID);
    const design = this.getDesign(newTemplateID);
    const newID = this.getTemplateByDesignAndLayout(design, layout);

    return newID || newTemplateID;
  },
  getLayoutEquivalent(originalTemplateID, newTemplateID) {
    const layout = this.getLayout(newTemplateID);
    const design = this.getDesign(originalTemplateID);
    const newID = this.getTemplateByDesignAndLayout(design, layout);
    return newID || newTemplateID;
  },
  layoutsForActiveView() {
    let section = 0;
    let position = '*';

    const prismItem = usePrismItemStore.getState().prismItem;

    if (window.activeView && prismItem && prismItem.pages && prismItem.pages[activeView.pageIndex]) {
      const page = prismItem.pages[activeView.pageIndex];
      section = page.product_section;
      if (this.positions().indexOf(page['page-is'])) {
        position = page['page-is'];
      }
      if (page.template && page.template.id) {
        position = this.getPosition(page.template.id) || position;
      }
    }
    return this.layoutsForPosition([position], section) || [];
  },
  getOrientation(size) {
    // Some custom components won't have size (e.g. <Envelope />)
    if (!size) return templateInterrogator.ORIENTATION.LANDSCAPE;

    const ratio = (size.width / size.height).toFixed(1);

    if (ratio == 1.0) return templateInterrogator.ORIENTATION.SQUARE;
    if (ratio < 1.0) return templateInterrogator.ORIENTATION.PORTRAIT;
    if (ratio > 1.0) return templateInterrogator.ORIENTATION.LANDSCAPE;
  },
};

builderDispatch.register((payload) => {
  if (payload.type === PrismUI.THEME_CHANGED) {
    TemplateStore.fetch();
  }
  if (payload.type == PrismUI.ITEM_LOADED) {
    TemplateStore.fetch();
  }
});

// export default TemplateStore;

export default TemplateStore;
